/**
 * Shared System Health presentation helpers: the daily score shape, the
 * "Improving / Declining / Stable" trend rules and the chart day label.
 * Moved out of FleetHubOverview so the System Health page owns them.
 */

export type SystemHealthDay = {
  date: string;
  telematics: number;
  orders: number;
  drivers: number;
  fuel: number;
};

export type HealthScoreKey = 'telematics' | 'orders' | 'drivers' | 'fuel';

export const SECTION_SCORE_KEY: Record<'fuel' | 'orders' | 'drivers' | 'gps', HealthScoreKey> = {
  gps: 'telematics',
  orders: 'orders',
  drivers: 'drivers',
  fuel: 'fuel',
};

/** Score points over recent momentum needed to call Improving / Declining (else Stable). */
export const TREND_DELTA_THRESHOLD = 5;
/** Compare latest day vs this many days earlier on the chart (recent momentum). */
export const TREND_LOOKBACK_DAYS = 3;

/** Order Management rate chart — indigo/sky (mode identity, not performance judgment). */
export const ORDERS_RATE_LINE_DELIVERY = '#818cf8';
export const ORDERS_RATE_LINE_COLLECTION = '#38bdf8';

export type HealthTrend = 'up' | 'down' | 'stable' | 'updating';
export type ScorePresentation = 'judgment' | 'label' | 'count';

export function trendBadgeShellClass(trend: HealthTrend): string {
  if (trend === 'up') return 'bg-green-500/20';
  if (trend === 'down') return 'bg-red-500/20';
  if (trend === 'updating') return 'bg-sky-500/20';
  return 'bg-gray-500/20';
}

export function trendBadgeTextClass(trend: HealthTrend): string {
  if (trend === 'up') return 'text-green-400';
  if (trend === 'down') return 'text-red-400';
  if (trend === 'updating') return 'text-sky-400';
  return 'text-gray-400';
}

export function trendBadgeLabel(trend: HealthTrend): string {
  if (trend === 'up') return 'Improving';
  if (trend === 'down') return 'Declining';
  if (trend === 'updating') return 'Updating';
  return 'Stable';
}

/** Directional badge from recent chart momentum (latest vs ~3 days earlier). */
export function trendFromSeries(values: Array<number | null | undefined>): 'up' | 'down' | 'stable' {
  const usable = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (usable.length < 2) return 'stable';
  const latestIdx = usable.length - 1;
  const baselineIdx = Math.max(0, latestIdx - TREND_LOOKBACK_DAYS);
  if (baselineIdx === latestIdx) return 'stable';
  const delta = usable[latestIdx] - usable[baselineIdx];
  if (delta >= TREND_DELTA_THRESHOLD) return 'up';
  if (delta <= -TREND_DELTA_THRESHOLD) return 'down';
  return 'stable';
}

/**
 * Directional badge for a raw count series (fill-ups, volumes) rather than a
 * 0–100 score. Uses a relative swing so it works whether the fleet does ~3 or
 * ~50 fill-ups a day; TREND_DELTA_THRESHOLD is score-points and meaningless here.
 */
export function trendFromCountSeries(values: Array<number | null | undefined>): 'up' | 'down' | 'stable' {
  const usable = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (usable.length < 2) return 'stable';
  const latestIdx = usable.length - 1;
  const baselineIdx = Math.max(0, latestIdx - TREND_LOOKBACK_DAYS);
  if (baselineIdx === latestIdx) return 'stable';
  const latest = usable[latestIdx];
  const baseline = usable[baselineIdx];
  const delta = latest - baseline;
  if (Math.abs(delta) < 2) return 'stable';
  const rel = baseline > 0 ? delta / baseline : delta > 0 ? 1 : -1;
  if (rel >= 0.25) return 'up';
  if (rel <= -0.25) return 'down';
  return 'stable';
}

const AXIS_WEEKDAY = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' });

/**
 * X-axis label for a `YYYY-MM-DD` day: "Mon" over "(20/10)". `label` stays
 * unique ("Mon 20/10"; the chart keys points by it) and AXIS_WEEKDAY_ONLY shows
 * its weekday; the chart draws `weekday` on the second line in brackets.
 */
export function dayAxisPoint(dateKey: string): { label: string; weekday?: string } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!m) return { label: dateKey };
  const day = AXIS_WEEKDAY.format(new Date(`${dateKey}T12:00:00.000Z`));
  const ddmm = `${m[3]}/${m[2]}`;
  return { label: `${day} ${ddmm}`, weekday: ddmm };
}

/** First line of a dayAxisPoint label: the weekday. */
export const AXIS_WEEKDAY_ONLY = (label: string) => label.split(' ')[0];
